import React, { useState, useEffect } from 'react';
import {
  Plus, Edit, Trash2, Loader2, RefreshCw,
  Star, X, Upload, Copy, Check, ExternalLink, Settings,
  Image as ImageIcon
} from 'lucide-react';
import { toast } from 'sonner';
import { pb } from '../lib/pocketbaseClient.js';
import { useAuth } from '../contexts/AuthContext.jsx';

const PLACEHOLDER_IMAGE = "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='400' height='300' viewBox='0 0 400 300'%3E%3Crect width='400' height='300' fill='%23f1f5f9'/%3E%3Ctext x='200' y='150' font-family='system-ui' font-size='18' fill='%2394a3b8' text-anchor='middle' dominant-baseline='middle'%3ENo Image%3C/text%3E%3C/svg%3E";

const GalleryList = () => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [title, setTitle] = useState('');
  const [category, setCategory] = useState('general');
  const [altText, setAltText] = useState('');
  const [description, setDescription] = useState('');
  const [assignedTo, setAssignedTo] = useState('none');
  const [active, setActive] = useState(true);
  const [file, setFile] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [copiedId, setCopiedId] = useState(null);
  const [editingItem, setEditingItem] = useState(null);
  const [showEditModal, setShowEditModal] = useState(false);
  const [saving, setSaving] = useState(false);
  const [showUploadForm, setShowUploadForm] = useState(false);
  const [editFile, setEditFile] = useState(null);
  const [editPreviewUrl, setEditPreviewUrl] = useState(null);
  const [uploadError, setUploadError] = useState('');

  const { role } = useAuth();
  const isAdmin = role === 'admin' || role === 'superadmin';

  const categories = [
    { value: 'hero', label: 'Hero Banner' },
    { value: 'home_about', label: 'Home About' },
    { value: 'about', label: 'About Page' },
    { value: 'portfolio', label: 'Portfolio' },
    { value: 'blog', label: 'Blog' },
    { value: 'general', label: 'General' },
  ];

  const sectionOptions = [
    { value: 'none', label: '-- Not assigned --' },
    { value: 'hero_banner', label: 'Hero Banner (Homepage)' },
    { value: 'home_about', label: 'Home About Section' },
    { value: 'about_page', label: 'About Us Page' },
  ];

  useEffect(() => {
    fetchItems();
  }, []);

  const fetchItems = async () => {
    setLoading(true);
    try {
      const records = await pb.collection('gallery_images').getList(1, 500, {
        sort: '-created',
      });
      console.log('📥 Fetched:', records.items.length, 'images');
      setItems(records.items);
    } catch (error) {
      console.error('Fetch error:', error);
      toast.error('Failed to load Gallery');
    } finally {
      setLoading(false);
    }
  };

  const getImageUrl = (item) => {
    if (!item || !item.image || item.image.length === 0) {
      return PLACEHOLDER_IMAGE;
    }
    try {
      let fileName = Array.isArray(item.image) ? item.image[0] : item.image;
      
      // Try PocketBase's built-in method first
      if (typeof pb.files?.getUrl === 'function') {
        return pb.files.getUrl(item, fileName);
      }
      
      // Fallback to manual URL construction
      const baseUrl = pb.baseUrl || 'https://housedesigns99.com';
      return `${baseUrl}/api/files/${item.collectionId}/${item.id}/${fileName}`;
    } catch (error) {
      console.error('❌ Error getting image URL:', error);
      return PLACEHOLDER_IMAGE;
    }
  };

  const handleFileChange = (e) => {
    const selectedFile = e.target.files[0];
    if (!selectedFile) return;
    if (!selectedFile.type.startsWith('image/')) {
      toast.error('Only image files are allowed');
      return;
    }
    if (selectedFile.size > 50 * 1024 * 1024) {
      toast.error('File too large (max 50MB)');
      return;
    }
    console.log('📎 File selected:', selectedFile.name);
    setFile(selectedFile);
    setPreviewUrl(URL.createObjectURL(selectedFile));
    setUploadError('');
  };

  const handleEditFileChange = (e) => {
    const selectedFile = e.target.files[0];
    if (!selectedFile) return;
    if (!selectedFile.type.startsWith('image/')) {
      toast.error('Only image files are allowed');
      return;
    }
    if (selectedFile.size > 50 * 1024 * 1024) {
      toast.error('File too large (max 50MB)');
      return;
    }
    setEditFile(selectedFile);
    setEditPreviewUrl(URL.createObjectURL(selectedFile));
  };

  const handleUpload = async () => {
    if (!title.trim()) {
      toast.error('Please enter a title');
      return;
    }
    if (!file) {
      toast.error('Please select an image');
      return;
    }

    setUploading(true);
    setUploadError('');
    
    try {
      const formData = new FormData();
      formData.append('title', title.trim());
      formData.append('category', category);
      formData.append('alt_text', altText.trim());
      formData.append('description', description.trim());
      formData.append('assignedTo', 'none');
      formData.append('active', String(active));
      formData.append('image', file);

      console.log('📤 Uploading:', file.name);
      
      const record = await pb.collection('gallery_images').create(formData);
      console.log('✅ Upload success:', record);
      
      toast.success('✅ Image uploaded successfully!');
      
      setTitle('');
      setCategory('general');
      setAltText('');
      setDescription('');
      setActive(true);
      setFile(null);
      setPreviewUrl(null);
      setShowUploadForm(false);
      await fetchItems();
      
      const fileInput = document.getElementById('image-file-input');
      if (fileInput) fileInput.value = '';
      
    } catch (error) {
      console.error('❌ Upload error:', error);
      setUploadError(error.message || 'Upload failed');
      toast.error(error.message || 'Upload failed');
    } finally {
      setUploading(false);
    }
  };

  const handleEdit = (item) => {
    setEditingItem(item);
    setTitle(item.title || '');
    setCategory(item.category || 'general');
    setAltText(item.alt_text || '');
    setDescription(item.description || '');
    setAssignedTo(item.assignedTo || 'none');
    setActive(item.active !== false);
    setEditFile(null);
    setEditPreviewUrl(null);
    setShowEditModal(true);
  };

  const handleSaveEdit = async () => {
    if (!editingItem) return;

    setSaving(true);
    try {
      const updateData = {
        title: title.trim(),
        category: category,
        alt_text: altText.trim(),
        description: description.trim(),
        assignedTo: assignedTo,
        active: active,
      };

      console.log('📝 Updating item:', editingItem.id, updateData);

      if (editFile) {
        const formData = new FormData();
        Object.keys(updateData).forEach(key => {
          formData.append(key, String(updateData[key]));
        });
        formData.append('image', editFile);
        await pb.collection('gallery_images').update(editingItem.id, formData);
      } else {
        await pb.collection('gallery_images').update(editingItem.id, updateData);
      }

      toast.success('✅ Image updated successfully');
      setShowEditModal(false);
      setEditingItem(null);
      setEditFile(null);
      setEditPreviewUrl(null);
      await fetchItems();
    } catch (error) {
      console.error('❌ Update error:', error);
      toast.error(error.message || 'Update failed');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this image?')) return;
    try {
      await pb.collection('gallery_images').delete(id);
      toast.success('✅ Deleted successfully');
      await fetchItems();
    } catch (error) {
      toast.error('Delete failed');
    }
  };

  const copyImageUrl = (item) => {
    const url = getImageUrl(item);
    navigator.clipboard.writeText(url);
    setCopiedId(item.id);
    toast.success('URL copied to clipboard');
    setTimeout(() => setCopiedId(null), 2000);
  };

  const openImageUrl = (item) => {
    const url = getImageUrl(item);
    window.open(url, '_blank');
  };

  const getAssignedImages = () => {
    const assigned = { hero_banner: null, home_about: null, about_page: null };
    items.forEach(img => {
      if (img.assignedTo === 'hero_banner') assigned.hero_banner = img;
      if (img.assignedTo === 'home_about') assigned.home_about = img;
      if (img.assignedTo === 'about_page') assigned.about_page = img;
    });
    return assigned;
  };

  const assignedImages = getAssignedImages();

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-amber-500" />
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto p-6">
      {/* Header */}
      <div className="flex justify-between items-center mb-6 flex-wrap gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-800">📸 Gallery Management</h1>
          <p className="text-slate-500 text-sm">Manage all images – upload, assign, and control visibility</p>
        </div>
        <div className="flex gap-2 flex-wrap">
          <button 
            onClick={fetchItems} 
            className="flex items-center gap-2 px-3 py-2 bg-slate-100 rounded-lg hover:bg-slate-200 transition"
          >
            <RefreshCw size={16} /> Refresh
          </button>
          <button 
            onClick={() => setShowUploadForm(!showUploadForm)} 
            className="flex items-center gap-2 px-4 py-2 bg-amber-500 text-white rounded-lg hover:bg-amber-600 transition"
          >
            <Plus size={18} /> {showUploadForm ? 'Close' : 'Upload New'}
          </button>
        </div>
      </div>

      {/* Section Assignment Panel */}
      <div className="mb-8 p-5 bg-indigo-50 rounded-xl border border-indigo-200">
        <h2 className="text-lg font-semibold mb-4 flex items-center gap-2">
          <Settings className="w-5 h-5 text-indigo-600" />
          Website Section Images (Active)
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="bg-white rounded-lg p-4 border border-indigo-100">
            <h3 className="font-semibold text-slate-800">🏠 Hero Banner</h3>
            {assignedImages.hero_banner ? (
              <div className="mt-2">
                <img 
                  src={getImageUrl(assignedImages.hero_banner)} 
                  alt={assignedImages.hero_banner.title} 
                  className="w-full h-24 object-cover rounded-lg"
                  onError={(e) => { e.target.src = PLACEHOLDER_IMAGE; }}
                />
                <p className="text-sm mt-1 truncate">{assignedImages.hero_banner.title}</p>
                <p className="text-xs text-green-600">✅ Active</p>
              </div>
            ) : (
              <div className="mt-2 p-4 bg-amber-50 rounded-lg border border-amber-200">
                <p className="text-sm text-amber-700">⚠️ No image assigned</p>
                <p className="text-xs text-amber-600 mt-1">Set assignedTo="hero_banner"</p>
              </div>
            )}
          </div>
          <div className="bg-white rounded-lg p-4 border border-indigo-100">
            <h3 className="font-semibold text-slate-800">📖 Home About Section</h3>
            {assignedImages.home_about ? (
              <div className="mt-2">
                <img 
                  src={getImageUrl(assignedImages.home_about)} 
                  alt={assignedImages.home_about.title} 
                  className="w-full h-24 object-cover rounded-lg"
                  onError={(e) => { e.target.src = PLACEHOLDER_IMAGE; }}
                />
                <p className="text-sm mt-1 truncate">{assignedImages.home_about.title}</p>
                <p className="text-xs text-green-600">✅ Active</p>
              </div>
            ) : (
              <div className="mt-2 p-4 bg-amber-50 rounded-lg border border-amber-200">
                <p className="text-sm text-amber-700">⚠️ No image assigned</p>
                <p className="text-xs text-amber-600 mt-1">Set assignedTo="home_about"</p>
              </div>
            )}
          </div>
          <div className="bg-white rounded-lg p-4 border border-indigo-100">
            <h3 className="font-semibold text-slate-800">ℹ️ About Us Page</h3>
            {assignedImages.about_page ? (
              <div className="mt-2">
                <img 
                  src={getImageUrl(assignedImages.about_page)} 
                  alt={assignedImages.about_page.title} 
                  className="w-full h-24 object-cover rounded-lg"
                  onError={(e) => { e.target.src = PLACEHOLDER_IMAGE; }}
                />
                <p className="text-sm mt-1 truncate">{assignedImages.about_page.title}</p>
                <p className="text-xs text-green-600">✅ Active</p>
              </div>
            ) : (
              <div className="mt-2 p-4 bg-amber-50 rounded-lg border border-amber-200">
                <p className="text-sm text-amber-700">⚠️ No image assigned</p>
                <p className="text-xs text-amber-600 mt-1">Set assignedTo="about_page"</p>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Upload Form */}
      {showUploadForm && (
        <div className="mb-8 p-5 bg-slate-50 rounded-xl border border-slate-200">
          <h2 className="text-lg font-semibold mb-4">Upload New Image</h2>
          {uploadError && (
            <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
              Error: {uploadError}
            </div>
          )}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium mb-1">Title *</label>
              <input 
                type="text" 
                value={title} 
                onChange={(e) => setTitle(e.target.value)} 
                placeholder="e.g., Hero Banner" 
                className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-amber-500" 
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Category</label>
              <select 
                value={category} 
                onChange={(e) => setCategory(e.target.value)} 
                className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-amber-500"
              >
                {categories.map((cat) => (
                  <option key={cat.value} value={cat.value}>{cat.label}</option>
                ))}
              </select>
            </div>
            <div className="md:col-span-2">
              <label className="block text-sm font-medium mb-1">Alt Text (SEO)</label>
              <input 
                type="text" 
                value={altText} 
                onChange={(e) => setAltText(e.target.value)} 
                placeholder="Describe the image for accessibility" 
                className="w-full px-3 py-2 border border-slate-300 rounded-lg" 
              />
            </div>
            <div className="md:col-span-2">
              <label className="block text-sm font-medium mb-1">Description</label>
              <textarea 
                value={description} 
                onChange={(e) => setDescription(e.target.value)} 
                rows="2" 
                placeholder="Optional description" 
                className="w-full px-3 py-2 border border-slate-300 rounded-lg" 
              />
            </div>
            <div className="md:col-span-2">
              <label className="block text-sm font-medium mb-1">Image File *</label>
              <input 
                id="image-file-input" 
                type="file" 
                accept="image/*" 
                onChange={handleFileChange} 
                className="w-full" 
              />
              {previewUrl && (
                <div className="mt-3">
                  <img src={previewUrl} alt="Preview" className="w-24 h-24 object-cover rounded-lg border" />
                </div>
              )}
            </div>
            <div className="flex items-center gap-3">
              <label className="flex items-center gap-2">
                <input 
                  type="checkbox" 
                  checked={active} 
                  onChange={(e) => setActive(e.target.checked)} 
                  className="w-4 h-4" 
                />
                <span className="text-sm">Active</span>
              </label>
            </div>
          </div>
          <button 
            onClick={handleUpload} 
            disabled={uploading || !file || !title} 
            className="mt-4 flex items-center gap-2 px-4 py-2 bg-amber-500 text-white rounded-lg hover:bg-amber-600 transition disabled:opacity-50"
          >
            {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload size={18} />}
            Upload Image
          </button>
        </div>
      )}

      {/* Gallery Grid */}
      {items.length === 0 ? (
        <div className="text-center py-16 bg-white rounded-2xl border border-slate-200">
          <ImageIcon className="w-16 h-16 text-slate-300 mx-auto mb-4" />
          <p className="text-slate-500">No images found. Upload your first image above.</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5">
          {items.map((item) => {
            const imageUrl = getImageUrl(item);
            const isAssigned = item.assignedTo && item.assignedTo !== 'none';
            const sectionLabel = sectionOptions.find(o => o.value === item.assignedTo)?.label || '';
            const hasImage = item.image && item.image.length > 0;

            return (
              <div key={item.id} className="bg-white rounded-xl overflow-hidden shadow-md border border-slate-200 hover:shadow-lg transition relative group">
                {isAssigned && (
                  <div className="absolute top-2 left-2 z-10">
                    <span className="px-2 py-0.5 bg-green-500 text-white text-xs rounded-full flex items-center gap-1">
                      <Star size={10} /> In Use
                    </span>
                  </div>
                )}
                <div className="relative h-48 bg-slate-100">
                  {hasImage ? (
                    <img
                      src={imageUrl}
                      alt={item.alt_text || item.title}
                      className="w-full h-full object-cover"
                      onError={(e) => { 
                        console.log('❌ Image load error for item:', item.id);
                        e.target.src = PLACEHOLDER_IMAGE;
                      }}
                    />
                  ) : (
                    <div className="w-full h-full flex flex-col items-center justify-center bg-slate-100">
                      <ImageIcon className="w-12 h-12 text-slate-300 mb-2" />
                      <span className="text-xs text-slate-400">No image</span>
                    </div>
                  )}
                  <div className="absolute top-2 right-2 flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                    <button 
                      onClick={() => copyImageUrl(item)} 
                      className="p-1.5 bg-white rounded-full shadow-md hover:bg-slate-100 transition" 
                      title="Copy URL"
                    >
                      {copiedId === item.id ? <Check size={14} className="text-green-600" /> : <Copy size={14} />}
                    </button>
                    <button 
                      onClick={() => openImageUrl(item)} 
                      className="p-1.5 bg-white rounded-full shadow-md hover:bg-slate-100 transition" 
                      title="Open in new tab"
                    >
                      <ExternalLink size={14} />
                    </button>
                    <button 
                      onClick={() => handleEdit(item)} 
                      className="p-1.5 bg-white rounded-full shadow-md hover:bg-indigo-100 transition" 
                      title="Edit"
                    >
                      <Edit size={14} />
                    </button>
                  </div>
                  {!item.active && (
                    <div className="absolute bottom-2 left-2 px-2 py-0.5 bg-black/60 text-white text-xs rounded-full">
                      Inactive
                    </div>
                  )}
                </div>
                <div className="p-3">
                  <h3 className="font-semibold text-slate-800 truncate">{item.title || 'Untitled'}</h3>
                  <p className="text-xs text-slate-500 mt-1">Category: {item.category || 'General'}</p>
                  <p className="text-xs text-slate-400 mt-0.5">assignedTo: {item.assignedTo || 'none'}</p>
                  {isAssigned && (
                    <p className="text-xs text-green-600 mt-1">✅ {sectionLabel}</p>
                  )}
                  <div className="flex justify-end mt-2">
                    <button 
                      onClick={() => handleDelete(item.id)} 
                      className="p-1.5 text-red-500 hover:bg-red-50 rounded-lg transition" 
                      title="Delete"
                    >
                      <Trash2 size={16} />
                    </button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Edit Modal */}
      {showEditModal && editingItem && (
        <div className="fixed inset-0 z-50 bg-black/50 backdrop-blur-sm flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl max-w-md w-full shadow-2xl max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-center p-5 border-b sticky top-0 bg-white z-10">
              <h2 className="text-xl font-bold">Edit Image</h2>
              <button onClick={() => setShowEditModal(false)} className="p-1 hover:bg-slate-100 rounded-lg">
                <X size={20} />
              </button>
            </div>
            <div className="p-5 space-y-4">
              <div>
                <label className="block text-sm font-medium mb-1">Current Image</label>
                <div className="w-full h-40 bg-slate-100 rounded-lg overflow-hidden border border-slate-200 flex items-center justify-center">
                  {editingItem.image && editingItem.image.length > 0 ? (
                    <img
                      src={getImageUrl(editingItem)}
                      alt={editingItem.title}
                      className="w-full h-full object-cover"
                      onError={(e) => { e.target.src = PLACEHOLDER_IMAGE; }}
                    />
                  ) : (
                    <div className="flex flex-col items-center text-slate-400">
                      <ImageIcon className="w-8 h-8 mb-1" />
                      <span className="text-xs">No image</span>
                    </div>
                  )}
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium mb-1">Change Image (optional)</label>
                <input 
                  type="file" 
                  accept="image/*" 
                  onChange={handleEditFileChange} 
                  className="w-full px-3 py-2 border border-slate-300 rounded-lg" 
                />
                {editPreviewUrl && (
                  <div className="mt-2">
                    <p className="text-xs text-green-600 mb-1">New preview:</p>
                    <img src={editPreviewUrl} alt="New preview" className="w-24 h-24 object-cover rounded-lg border border-green-300" />
                  </div>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium mb-1">Title</label>
                <input 
                  type="text" 
                  value={title} 
                  onChange={(e) => setTitle(e.target.value)} 
                  className="w-full px-3 py-2 border border-slate-300 rounded-lg" 
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Category</label>
                <select 
                  value={category} 
                  onChange={(e) => setCategory(e.target.value)} 
                  className="w-full px-3 py-2 border border-slate-300 rounded-lg"
                >
                  {categories.map((cat) => (
                    <option key={cat.value} value={cat.value}>{cat.label}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Alt Text</label>
                <input 
                  type="text" 
                  value={altText} 
                  onChange={(e) => setAltText(e.target.value)} 
                  className="w-full px-3 py-2 border border-slate-300 rounded-lg" 
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Description</label>
                <textarea 
                  value={description} 
                  onChange={(e) => setDescription(e.target.value)} 
                  rows="2" 
                  className="w-full px-3 py-2 border border-slate-300 rounded-lg" 
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Assign to Section</label>
                <select 
                  value={assignedTo} 
                  onChange={(e) => setAssignedTo(e.target.value)} 
                  className="w-full px-3 py-2 border border-slate-300 rounded-lg"
                >
                  {sectionOptions.map((opt) => (
                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                  ))}
                </select>
                <p className="text-xs text-slate-500 mt-1">
                  ⚠️ Hero Banner এর জন্য "Hero Banner (Homepage)" সিলেক্ট করুন
                </p>
              </div>
              <div className="flex items-center gap-3">
                <label className="flex items-center gap-2">
                  <input 
                    type="checkbox" 
                    checked={active} 
                    onChange={(e) => setActive(e.target.checked)} 
                    className="w-4 h-4" 
                  />
                  <span className="text-sm">Active</span>
                </label>
              </div>
            </div>
            <div className="flex justify-end gap-3 p-5 border-t sticky bottom-0 bg-white">
              <button 
                onClick={() => setShowEditModal(false)} 
                className="px-4 py-2 border border-slate-300 rounded-lg text-slate-700 hover:bg-slate-50"
              >
                Cancel
              </button>
              <button 
                onClick={handleSaveEdit} 
                disabled={saving} 
                className="px-4 py-2 bg-amber-500 text-white rounded-lg hover:bg-amber-600 flex items-center gap-2 disabled:opacity-50"
              >
                {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Edit size={16} />}
                Save Changes
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default GalleryList;